import React, { useEffect, useState } from 'react';
import { placesApi } from '../api/client';
import type { Place } from '../api/client';
import './Places.css';

const Places: React.FC = () => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPlace, setNewPlace] = useState({
    name: '',
    city: '',
    country: '',
    latitude: '',
    longitude: ''
  });

  useEffect(() => {
    fetchPlaces();
  }, []);

  const fetchPlaces = async () => {
    try {
      setLoading(true);
      const response = await placesApi.getPlaces();
      setPlaces(response.data);
    } catch (err) {
      setError('Failed to load places');
      console.error('Places error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlace = async () => {
    if (!newPlace.name.trim() || !newPlace.city.trim() || !newPlace.country.trim()) return;

    try {
      const placeData = {
        name: newPlace.name,
        city: newPlace.city,
        country: newPlace.country,
        latitude: newPlace.latitude ? parseFloat(newPlace.latitude) : undefined,
        longitude: newPlace.longitude ? parseFloat(newPlace.longitude) : undefined
      };

      await placesApi.createPlace(placeData);
      await fetchPlaces();
      setShowAddModal(false);
      resetNewPlace();
    } catch (err) {
      console.error('Error adding place:', err);
      setError('Failed to add place');
    }
  };

  const handleDeletePlace = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this place?')) return;

    try {
      await placesApi.deletePlace(id);
      await fetchPlaces();
    } catch (err) {
      console.error('Error deleting place:', err);
      setError('Failed to delete place');
    }
  };

  const resetNewPlace = () => {
    setNewPlace({
      name: '',
      city: '',
      country: '',
      latitude: '',
      longitude: ''
    });
  };

  const getCountryFlag = (country: string) => {
    const flags: { [key: string]: string } = {
      'usa': '🇺🇸',
      'united states': '🇺🇸',
      'france': '🇫🇷',
      'japan': '🇯🇵',
      'australia': '🇦🇺',
      'uk': '🇬🇧',
      'united kingdom': '🇬🇧',
      'canada': '🇨🇦',
      'germany': '🇩🇪',
      'italy': '🇮🇹',
      'spain': '🇪🇸'
    };
    return flags[country.toLowerCase()] || '🌍';
  };

  const openInMaps = (place: Place) => {
    if (place.latitude && place.longitude) {
      const url = `https://www.google.com/maps?q=${place.latitude},${place.longitude}`;
      window.open(url, '_blank');
    } else {
      const query = `${place.name}, ${place.city}, ${place.country}`;
      const url = `https://www.google.com/maps/search/${encodeURIComponent(query)}`;
      window.open(url, '_blank');
    }
  };

  if (loading) {
    return <div className="places-page"><div className="loading">Loading places...</div></div>;
  }

  if (error) {
    return <div className="places-page"><div className="error">Error: {error}</div></div>;
  }

  return (
    <div className="places-page">
      <header className="page-header">
        <h1>📍 Places</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddModal(true)}
        >
          Add New Place
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="places-content">
        <div className="places-stats">
          <div className="stat-card">
            <span className="stat-number">{places.length}</span>
            <span className="stat-label">Total Places</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{new Set(places.map(p => p.country)).size}</span>
            <span className="stat-label">Countries</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{new Set(places.map(p => p.city)).size}</span>
            <span className="stat-label">Cities</span>
          </div>
        </div>

        <div className="places-grid">
          {places.map((place) => (
            <div key={place.id} className="place-card">
              <div className="place-header">
                <div className="place-title">
                  <span className="country-flag">{getCountryFlag(place.country)}</span>
                  <h3 className="place-name">{place.name}</h3>
                </div>
                <button 
                  className="delete-btn"
                  onClick={() => handleDeletePlace(place.id)}
                  title="Delete place"
                >
                  ×
                </button>
              </div>

              <div className="place-content">
                <div className="location-info">
                  <div className="location-row">
                    <span className="location-icon">🏙️</span>
                    <span className="location-text">{place.city}</span>
                  </div>
                  <div className="location-row">
                    <span className="location-icon">🌍</span>
                    <span className="location-text">{place.country}</span>
                  </div>
                </div>

                {place.latitude && place.longitude && (
                  <div className="coordinates">
                    <span className="coords-label">Coordinates:</span>
                    <span className="coords-value">
                      {place.latitude.toFixed(6)}, {place.longitude.toFixed(6)}
                    </span>
                  </div>
                )}

                <div className="place-actions">
                  <button 
                    className="btn btn-small btn-outline"
                    onClick={() => openInMaps(place)}
                  >
                    🗺️ View on Maps
                  </button>
                </div>
              </div>
            </div>
          ))}

          {places.length === 0 && (
            <div className="no-places">
              <span className="no-places-icon">📍</span>
              <p>No places added yet. Add your first place to get started!</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Place Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Place</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Place Name</label>
                <input
                  type="text"
                  value={newPlace.name}
                  onChange={(e) => setNewPlace({...newPlace, name: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Eiffel Tower, Central Park..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={newPlace.city}
                    onChange={(e) => setNewPlace({...newPlace, city: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Paris"
                  />
                </div>

                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    value={newPlace.country}
                    onChange={(e) => setNewPlace({...newPlace, country: e.target.value})}
                    className="form-input"
                    placeholder="e.g., France"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Latitude (optional)</label>
                  <input
                    type="number"
                    step="any"
                    value={newPlace.latitude}
                    onChange={(e) => setNewPlace({...newPlace, latitude: e.target.value})}
                    className="form-input"
                    placeholder="e.g., 48.858844"
                  />
                </div>

                <div className="form-group">
                  <label>Longitude (optional)</label>
                  <input
                    type="number"
                    step="any"
                    value={newPlace.longitude}
                    onChange={(e) => setNewPlace({...newPlace, longitude: e.target.value})}
                    className="form-input"
                    placeholder="e.g., 2.294351"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-success"
                onClick={handleAddPlace}
                disabled={!newPlace.name.trim() || !newPlace.city.trim() || !newPlace.country.trim()}
              >
                Add Place
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Places;